// Phase 1a — Assets store (Zustand).
// Caches the asset-type registry and location lookup (both rarely change; expensive to re-fetch
// on every NewAssetForm open or AssetsPage filter interaction). Asset list itself stays in
// page-local state — matches the OrdersPage pattern.

import { create } from 'zustand';
import { apiFetchAuthed } from '../apiHelpers';
import { AssetType, Location, AssetsFilterState, DEFAULT_FILTERS } from '../Assets/types';

interface AssetsState {
  // Cached lookups
  types: AssetType[];
  typesLoaded: boolean;
  locations: Location[];
  locationsLoaded: boolean;

  // Filter state that survives in-app navigation (page → detail → back).
  filters: AssetsFilterState;

  // Actions
  ensureTypesLoaded: () => Promise<void>;
  ensureLocationsLoaded: () => Promise<void>;
  refreshTypes: () => Promise<void>;
  refreshLocations: () => Promise<void>;
  setFilters: (patch: Partial<AssetsFilterState>) => void;
  resetFilters: () => void;
}

const useAssetsStore = create<AssetsState>((set, get) => ({
  types: [],
  typesLoaded: false,
  locations: [],
  locationsLoaded: false,
  filters: { ...DEFAULT_FILTERS },

  ensureTypesLoaded: async () => {
    if (get().typesLoaded) return;
    await get().refreshTypes();
  },

  ensureLocationsLoaded: async () => {
    if (get().locationsLoaded) return;
    await get().refreshLocations();
  },

  refreshTypes: async () => {
    const data: AssetType[] = await apiFetchAuthed('/asset-types');
    set({ types: data, typesLoaded: true });
  },

  refreshLocations: async () => {
    const data: Location[] = await apiFetchAuthed('/locations');
    set({ locations: data, locationsLoaded: true });
  },

  setFilters: (patch) => {
    set((state) => ({ filters: { ...state.filters, ...patch } }));
  },

  resetFilters: () => {
    set({ filters: { ...DEFAULT_FILTERS } });
  },
}));

export default useAssetsStore;
